<template>
  <div class="h-screen w-screen bg-neutral-950/25 dark:bg-black/50 backdrop-blur-[2px] absolute top-0 left-0 p-4 overflow-hidden flex justify-center min-h-568 min-w-[375px] z-90 modal-back-fade-in text-xs"
  @drop.prevent
  @dragover.prevent
  @dragenter.prevent
  @dragleave.prevent
  >
    <div ref="modalContainer" class="w-full h-full min-h-0 flex flex-col flex-grow p-1 items-stretch justify-center overflow-y-auto">
      <div
        class="w-full max-h-full min-h-0 flex flex-col flex-grow overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-2xl dark:shadow-black/60 modal-fade-in self-center"
        :class="{ 'max-w-7xl': !showFull }"
      >
      <!-- Planned sign-out countdown: the top bar's copy sits under this Modal's backdrop, so the dialog shows its own -->
        <SignOutBar />
        <div v-if="showTitle" class="modal-header shrink-0">
          <div class="w-full min-w-0 text-left truncate text-lg font-semibold">{{title}}</div>
          <button class="bn-icon-only text-sky flex-shrink-0 w-7 h-7 [&>svg]:w-5 [&>svg]:h-5" aria-label="Close" @click="closeModal()" :disabled="isModalBlocked"><XIcon /></button>
        </div>
        <div
          class="w-full flex flex-col flex-1 min-h-0 overflow-hidden px-3 pb-3 items-stretch self-stretch"
          :class="!showTitle ? 'pt-5' : null"
        >
          <slot name="content" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, nextTick, inject } from 'vue';
import SignOutBar from './SignOutBar.vue'
import {XIcon} from "@heroicons/vue/outline"

export default {
  props:{
    title:{type:String,default:null},
    showTitle:{type:Boolean,default:true},
    showFull:{type:Boolean,default:false}
  },
  emits:["closeModal"],
  components:{XIcon, SignOutBar},
  setup(props,{emit}) {
    let previousTitle
    const global = inject('global')
    const {isModalBlocked} = global
    const modalContainer = ref(null);

    // Snapshot focusable elements FRESH on every Tab keypress. A prior implementation
    // cached this in a Vue computed property whose only dependency was modalContainer.value
    // (set once at mount), so the snapshot never refreshed when the modal's contents changed
    // — multi-step modals like BomImport would lose tab nav after switching steps because the
    // trap thought the live input wasn't in the focusable set and teleported focus to a stale
    // node. querySelectorAll on a modal-sized subtree is cheap and runs only at keypress rate.
    const collectFocusableElements = () => {
      if (!modalContainer.value) return [];
      const elements = Array.from(modalContainer.value.querySelectorAll(
        'a[href], area[href], input:not([disabled]):not([type="file"][hidden]), select:not([disabled]), textarea:not([disabled]), ' +
        'button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
      ));
      // Sort by tabindex: positives first ascending, then 0/unset in DOM order.
      elements.sort((a, b) => {
        const ta = parseInt(a.getAttribute('tabindex') || 0, 10);
        const tb = parseInt(b.getAttribute('tabindex') || 0, 10);
        if (ta > 0 && tb > 0) return ta - tb;
        if (ta === 0 && tb === 0) return 0;
        if (ta > 0) return -1;
        if (tb > 0) return 1;
        return 0;
      });
      return elements;
    };

    const trapFocus = (e) => {
      if (!modalContainer.value) return;
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      // Something inside the modal already handled this Tab — a rich-text editor indenting,
      // for example. This listener is on document, so it runs after the element's own handler
      // and would otherwise override it: preventDefault stops the browser tabbing, but not an
      // explicit .focus() call here. Editors are [contenteditable] and therefore usually the
      // LAST focusable in the modal, which is exactly the branch that teleports to the first.
      if (e.defaultPrevented) return;

      const focusableElementsArray = collectFocusableElements();
      if (focusableElementsArray.length === 0) return;

      const firstFocusableElement = focusableElementsArray[0];
      const lastFocusableElement = focusableElementsArray[focusableElementsArray.length - 1];

      const currentFocus = document.activeElement;
      if (currentFocus?.type === 'datetime-local') {
        // Skip trapping for datetime-local — its native picker uses its own tab cycling.
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement || !focusableElementsArray.includes(document.activeElement)) {
          e.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement || !focusableElementsArray.includes(document.activeElement)) {
          e.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    onMounted(() => {
      previousTitle = document.title
      if(props.title) {document.title = props.title}
      nextTick(() => {
        document.addEventListener('keydown', trapFocus);
        //if (modalContainer.value && modalContainer.value.querySelector) {
        //  const firstFocusableElement = modalContainer.value.querySelector(
        //    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), ' +
        //    'button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
        //  );
        //  if (firstFocusableElement) {
        //    firstFocusableElement.focus();
        //  }
        //}
      });
    });

    onUnmounted(() => {
      document.title = previousTitle
      document.removeEventListener('keydown', trapFocus);
    });

    const closeModal = () => {
      document.title = previousTitle
      emit("closeModal")
    }

    return {
      modalContainer,
      closeModal,
      isModalBlocked
    };
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-fade-in {
  animation: fadeIn 0.7s ease-out;
}

.modal-back-fade-in {
  animation: fadeIn 0.3s ease-out;
}

</style>